import React, { useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import { FIRESTORE_UTMS_TABLE } from "constants/FirestoreConstant";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import { UTM_MEDIUMS, UTM_SOURCES } from "../../../../constants/MarketingConstants";

const { Option } = Select;
const { Text, Title } = Typography;

const UTMTool = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const utms = useSelector((state) => state.firestoreReducer.ordered.utms);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState({ visible: false, utm: null });
  const [editModal, setEditModal] = useState({ visible: false, utm: null });
  const [editForm] = Form.useForm();

  const utmTooltips = {
    name: t("explanations.utm_tooltip_name"),
    source: t("explanations.utm_tooltip_source"),
    medium: t("explanations.utm_tooltip_medium"),
    campaign: t("explanations.utm_tooltip_campaign"),
    term: t("explanations.utm_tooltip_term"),
    content: t("explanations.utm_tooltip_content"),
  };

  const transformToUtmValue = (input) => {
    if (!input) return "";
    return input
      .toLowerCase()
      .replace(/[^a-z0-9_\s]/g, "") // Keep underscores, remove other special chars
      .replace(/\s+/g, "_") // Replace spaces with underscores
      .trim();
  };

  const generateFullUrl = (utmParams) => {
    const baseUrl = "https://hetvleescentrum.nl";
    const params = new URLSearchParams();

    if (utmParams.source) params.append("utm_source", utmParams.source);
    if (utmParams.medium) params.append("utm_medium", utmParams.medium);
    if (utmParams.campaign) params.append("utm_campaign", utmParams.campaign);
    if (utmParams.term) params.append("utm_term", utmParams.term);
    if (utmParams.content) params.append("utm_content", utmParams.content);

    return `${baseUrl}?${params.toString()}`;
  };

  const handleSaveUtm = async (values) => {
    setLoading(true);
    try {
      const utmData = {
        name: values.name,
        source: values.sourceType === "other" ? values.sourceCustom : values.sourceType,
        medium: values.mediumType === "other" ? values.mediumCustom : values.mediumType,
        campaign: transformToUtmValue(values.campaign),
        term: transformToUtmValue(values.term),
        content: transformToUtmValue(values.content),
        createdAt: firestore.FieldValue.serverTimestamp(),
      };

      await firestore.collection(FIRESTORE_UTMS_TABLE).add(utmData);
      message.success(t("utm_saved"));
      form.resetFields();
    } catch (error) {
      console.error("Error saving UTM:", error);
      message.error(t("error_saving_utm"));
    } finally {
      setLoading(false);
    }
  };

  const handleEditUtm = async (values) => {
    setLoading(true);
    try {
      const utmData = {
        name: values.name,
        source: values.sourceType === "other" ? values.sourceCustom : values.sourceType,
        medium: values.mediumType === "other" ? values.mediumCustom : values.mediumType,
        campaign: transformToUtmValue(values.campaign),
        term: transformToUtmValue(values.term),
        content: transformToUtmValue(values.content),
        updatedAt: firestore.FieldValue.serverTimestamp(),
      };

      await firestore.collection(FIRESTORE_UTMS_TABLE).doc(editModal.utm.id).update(utmData);
      message.success(t("utm_updated"));
      setEditModal({ visible: false, utm: null });
      editForm.resetFields();
    } catch (error) {
      console.error("Error updating UTM:", error);
      message.error(t("error_updating_utm"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUtm = async (utmId) => {
    try {
      await firestore.collection(FIRESTORE_UTMS_TABLE).doc(utmId).delete();
      message.success(t("utm_deleted"));
    } catch (error) {
      console.error("Error deleting UTM:", error);
      message.error(t("error_deleting_utm"));
    }
  };

  const copyUrl = (utm) => {
    const url = generateFullUrl(utm);
    navigator.clipboard.writeText(url);
    message.success(t("url_copied"));
  };

  const showDetail = (utm) => {
    setDetailModal({ visible: true, utm });
  };

  const showEdit = (utm) => {
    setEditModal({ visible: true, utm });
    editForm.setFieldsValue({
      name: utm.name,
      sourceType: UTM_SOURCES.find((s) => s.value === utm.source) ? utm.source : "other",
      sourceCustom: UTM_SOURCES.find((s) => s.value === utm.source) ? "" : utm.source,
      mediumType: UTM_MEDIUMS.find((m) => m.value === utm.medium) ? utm.medium : "other",
      mediumCustom: UTM_MEDIUMS.find((m) => m.value === utm.medium) ? "" : utm.medium,
      campaign: utm.campaign,
      term: utm.term,
      content: utm.content,
    });
  };

  const createCampaignWithUtm = (utm) => {
    const campaignData = {
      utmSource: utm.source,
      utmMedium: utm.medium,
      utmCampaign: utm.campaign,
      utmTerm: utm.term,
      utmContent: utm.content,
    };

    navigate(urls.addCampaign, { state: { utmData: campaignData } });
  };

  const renderFormItemWithTooltip = (label, tooltip, children) => (
    <Form.Item
      label={
        <Space>
          {label}
          <Tooltip title={tooltip} placement="right">
            <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
          </Tooltip>
        </Space>
      }
    >
      {children}
    </Form.Item>
  );

  if (!utms) {
    return <Loading cover="content" />;
  }

  return (
    <div>
      <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title={t("create_utm")}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveUtm}
              initialValues={{ sourceType: "facebook", mediumType: "social" }}
            >
              {renderFormItemWithTooltip(
                t("utm_name"),
                utmTooltips.name,
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: t("please_enter_utm_name") }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder={t("enter_utm_name")} />
                </Form.Item>,
              )}

              {renderFormItemWithTooltip(
                t("utm_source"),
                utmTooltips.source,
                <Form.Item
                  name="sourceType"
                  rules={[{ required: true, message: t("please_select_source") }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder={t("select_source")}>
                    {UTM_SOURCES.map((source) => (
                      <Option key={source.value} value={source.value}>
                        {source.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>,
              )}

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.sourceType !== currentValues.sourceType}
              >
                {({ getFieldValue }) =>
                  getFieldValue("sourceType") === "other" ? (
                    <Form.Item
                      label={t("custom_source")}
                      name="sourceCustom"
                      rules={[{ required: true, message: t("please_enter_custom_source") }]}
                    >
                      <Input placeholder={t("enter_custom_source")} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>

              {renderFormItemWithTooltip(
                t("utm_medium"),
                utmTooltips.medium,
                <Form.Item
                  name="mediumType"
                  rules={[{ required: true, message: t("please_select_medium") }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select placeholder={t("select_medium")}>
                    {UTM_MEDIUMS.map((medium) => (
                      <Option key={medium.value} value={medium.value}>
                        {medium.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>,
              )}

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.mediumType !== currentValues.mediumType}
              >
                {({ getFieldValue }) =>
                  getFieldValue("mediumType") === "other" ? (
                    <Form.Item
                      label={t("custom_medium")}
                      name="mediumCustom"
                      rules={[{ required: true, message: t("please_enter_custom_medium") }]}
                    >
                      <Input placeholder={t("enter_custom_medium")} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>

              {renderFormItemWithTooltip(
                t("campaign_name"),
                utmTooltips.campaign,
                <Form.Item
                  name="campaign"
                  rules={[{ required: true, message: t("please_enter_campaign_name") }]}
                  help={t("campaign_name_help")}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder={t("enter_campaign_name_example")} />
                </Form.Item>,
              )}

              {renderFormItemWithTooltip(
                t("utm_term"),
                utmTooltips.term,
                <Form.Item name="term" help={t("utm_term_help")} style={{ marginBottom: 0 }}>
                  <Input placeholder={t("enter_utm_term_example")} />
                </Form.Item>,
              )}

              {renderFormItemWithTooltip(
                t("utm_content"),
                utmTooltips.content,
                <Form.Item name="content" help={t("utm_content_help")} style={{ marginBottom: 0 }}>
                  <Input placeholder={t("enter_utm_content_example")} />
                </Form.Item>,
              )}

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  {t("save_utm")}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t("saved_utms")}>
            <List
              dataSource={utms}
              renderItem={(utm) => (
                <List.Item
                  actions={[
                    <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(utm)} />,
                    <Button size="small" icon={<EditOutlined />} onClick={() => showEdit(utm)} />,
                    <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(utm)} />,
                    <Popconfirm title={t("confirm_delete")} onConfirm={() => handleDeleteUtm(utm.id)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    title={utm.name}
                    description={
                      <Space>
                        <Tag>{utm.source}</Tag>
                        <Tag>{utm.medium}</Tag>
                        <Tag>{utm.campaign}</Tag>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Detail Modal */}
      <Modal
        title={t("utm_details")}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false, utm: null })}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => copyUrl(detailModal.utm)}>
            {t("copy_url")}
          </Button>,
          <Button
            key="campaign"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => createCampaignWithUtm(detailModal.utm)}
          >
            {t("create_campaign_with_utm")}
          </Button>,
        ]}
      >
        {detailModal.utm && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t("complete_url")}:</Text>
              <br />
              <Text code copyable style={{ wordBreak: "break-all" }}>
                {generateFullUrl(detailModal.utm)}
              </Text>
            </div>

            <div style={{ marginBottom: 8 }}>
              <Text strong>UTM Source:</Text> <Tag>{detailModal.utm.source}</Tag>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>UTM Medium:</Text> <Tag>{detailModal.utm.medium}</Tag>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>UTM Campaign:</Text> <Tag>{detailModal.utm.campaign}</Tag>
            </div>
            {detailModal.utm.term && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>UTM Term:</Text> <Tag>{detailModal.utm.term}</Tag>
              </div>
            )}
            {detailModal.utm.content && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>UTM Content:</Text> <Tag>{detailModal.utm.content}</Tag>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={t("edit_utm")}
        open={editModal.visible}
        onCancel={() => {
          setEditModal({ visible: false, utm: null });
          editForm.resetFields();
        }}
        footer={null}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditUtm}>
          {renderFormItemWithTooltip(
            t("utm_name"),
            utmTooltips.name,
            <Form.Item
              name="name"
              rules={[{ required: true, message: t("please_enter_utm_name") }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder={t("enter_utm_name")} />
            </Form.Item>,
          )}

          {renderFormItemWithTooltip(
            t("utm_source"),
            utmTooltips.source,
            <Form.Item
              name="sourceType"
              rules={[{ required: true, message: t("please_select_source") }]}
              style={{ marginBottom: 0 }}
            >
              <Select placeholder={t("select_source")}>
                {UTM_SOURCES.map((source) => (
                  <Option key={source.value} value={source.value}>
                    {source.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>,
          )}

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.sourceType !== currentValues.sourceType}
          >
            {({ getFieldValue }) =>
              getFieldValue("sourceType") === "other" ? (
                <Form.Item
                  label={t("custom_source")}
                  name="sourceCustom"
                  rules={[{ required: true, message: t("please_enter_custom_source") }]}
                >
                  <Input placeholder={t("enter_custom_source")} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          {renderFormItemWithTooltip(
            t("utm_medium"),
            utmTooltips.medium,
            <Form.Item
              name="mediumType"
              rules={[{ required: true, message: t("please_select_medium") }]}
              style={{ marginBottom: 0 }}
            >
              <Select placeholder={t("select_medium")}>
                {UTM_MEDIUMS.map((medium) => (
                  <Option key={medium.value} value={medium.value}>
                    {medium.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>,
          )}

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.mediumType !== currentValues.mediumType}
          >
            {({ getFieldValue }) =>
              getFieldValue("mediumType") === "other" ? (
                <Form.Item
                  label={t("custom_medium")}
                  name="mediumCustom"
                  rules={[{ required: true, message: t("please_enter_custom_medium") }]}
                >
                  <Input placeholder={t("enter_custom_medium")} />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          {renderFormItemWithTooltip(
            t("campaign_name"),
            utmTooltips.campaign,
            <Form.Item
              name="campaign"
              rules={[{ required: true, message: t("please_enter_campaign_name") }]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder={t("enter_campaign_name_example")} />
            </Form.Item>,
          )}

          {renderFormItemWithTooltip(
            t("utm_term"),
            utmTooltips.term,
            <Form.Item name="term" style={{ marginBottom: 0 }}>
              <Input placeholder={t("enter_utm_term_example")} />
            </Form.Item>,
          )}

          {renderFormItemWithTooltip(
            t("utm_content"),
            utmTooltips.content,
            <Form.Item name="content" style={{ marginBottom: 0 }}>
              <Input placeholder={t("enter_utm_content_example")} />
            </Form.Item>,
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t("update_utm")}
              </Button>
              <Button
                onClick={() => {
                  setEditModal({ visible: false, utm: null });
                  editForm.resetFields();
                }}
              >
                {t("cancel")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UTMTool;
